import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  AlertCircle, Package, RefreshCw, CheckCircle2, ShoppingCart, Trash2, ArrowRightLeft, Search,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Backorder() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(null);
  const [reemplazo, setReemplazo] = useState(null); // {sol, linea, idx}
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sales/backorder");
      setItems(data);
    } catch (e) {
      toast.error("No se pudo cargar backorder");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleGenerar = async (sol) => {
    if (!sol.hay_stock_para_np_complementaria) {
      toast.error("Sin stock disponible para generar NP complementaria");
      return;
    }
    setGenerating(sol.id);
    try {
      const { data } = await api.post(`/sales/backorder/${sol.id}/generar-np-complementaria`);
      toast.success(`NP ${data.pedido.numero} generada con ${data.cantidad_lineas_generadas} línea(s)`);
      nav(`/pedidos/${data.pedido.id}/editar`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenerating(null);
    }
  };

  // ITER221: eliminar pendiente completo o una sola línea
  const eliminarSolicitud = async (sol) => {
    if (!window.confirm(`¿Eliminar el pendiente ${sol.numero}?\n\nDejará de aparecer en esta lista y no se generarán más NP complementarias.`)) return;
    try {
      await api.delete(`/sales/backorder/${sol.id}`);
      toast.success(`${sol.numero} eliminado de pendientes`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const eliminarLinea = async (sol, idx, li) => {
    if (!window.confirm(`¿Quitar "${li.producto_nombre}" del pendiente ${sol.numero}?`)) return;
    try {
      await api.delete(`/sales/backorder/${sol.id}/linea/${idx}`);
      toast.success("Producto quitado del pendiente");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-4 space-y-4" data-testid="backorder-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#173d34]">Pedidos pendientes por stock</h1>
          <p className="text-sm text-[#5c7368]">Genera NP complementarias, reemplaza productos que ya ingresaron o elimina pendientes</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="backorder-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Recargar
        </Button>
      </div>

      {loading && items.length === 0 && <p className="text-sm text-[#5c7368]">Cargando...</p>}
      {!loading && items.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-[#5c7368]">
            <CheckCircle2 className="w-12 h-12 mx-auto text-[#4a8f5f] mb-2" />
            No hay pedidos pendientes por stock.
          </CardContent>
        </Card>
      )}

      {items.map((sol) => (
        <Card key={sol.id} data-testid={`backorder-item-${sol.numero}`}>
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <Package className="w-4 h-4" /> {sol.numero}
                  <Badge variant={sol.estado === "parcial" ? "secondary" : "outline"} className="text-xs">
                    {sol.estado}
                  </Badge>
                </CardTitle>
                <p className="text-sm text-[#5c7368] mt-1">
                  Cliente: <b>{sol.cliente_nombre || "—"}</b>
                  {sol.cotizacion_numero && <span> · Cot. {sol.cotizacion_numero}</span>}
                  {sol.pedido_numero && <span> · Pedido original {sol.pedido_numero}</span>}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => handleGenerar(sol)}
                  disabled={!sol.hay_stock_para_np_complementaria || generating === sol.id}
                  data-testid={`generar-np-comp-${sol.numero}`}
                  className="bg-[#173d34] hover:bg-[#0f2924] text-white"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  {generating === sol.id ? "Generando..." : "Generar NP complementaria"}
                </Button>
                <Button variant="outline" onClick={() => eliminarSolicitud(sol)}
                  className="text-[#7a2514] border-[#d99584]" data-testid={`eliminar-pendiente-${sol.numero}`}>
                  <Trash2 className="w-4 h-4 mr-2" />Eliminar pendiente
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
              <div className="bg-[#f5f2ea] p-2 rounded">
                <div className="text-[#5c7368]">Líneas pendientes</div>
                <div className="font-semibold">{sol.total_lineas_pendientes}</div>
              </div>
              <div className="bg-[#f5f2ea] p-2 rounded">
                <div className="text-[#5c7368]">Cantidad pendiente</div>
                <div className="font-semibold">{sol.total_cantidad_pendiente}</div>
              </div>
              <div className={`p-2 rounded ${sol.total_disponible_ahora > 0 ? "bg-[#e3efd8] text-[#2d5c34]" : "bg-[#f5f2ea]"}`}>
                <div className="text-xs">Disponible ahora</div>
                <div className="font-semibold">{sol.total_disponible_ahora}</div>
              </div>
            </div>

            <div className="space-y-1.5">
              {sol.lineas.map((li, i) => (
                <div key={i} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 py-2 px-2 rounded border border-[#e6e0d0] text-sm"
                  data-testid={`backorder-linea-${sol.numero}-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{li.producto_nombre}</div>
                    <div className="text-xs text-[#5c7368]">
                      {li.laboratorio_nombre || "—"} · {li.presentacion || "—"}
                      {li.reemplazado_de && <span className="text-[#8a5500]"> · antes: {li.reemplazado_de}</span>}
                    </div>
                  </div>
                  <div className="flex items-center justify-between sm:justify-end gap-3">
                    <div className="text-right text-xs">
                      <div>Pend: <b>{li.cantidad_pendiente}</b> {li.unidad_venta}</div>
                      <div className={li.puede_despachar ? "text-[#2d5c34] font-semibold" : "text-[#b53a25]"}>
                        {li.puede_despachar ? (
                          <><CheckCircle2 className="w-3 h-3 inline mr-1" />Listo: {li.disponible_ahora}</>
                        ) : (
                          <><AlertCircle className="w-3 h-3 inline mr-1" />Sin stock</>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => setReemplazo({ sol, li, idx: i })}
                        data-testid={`reemplazar-${sol.numero}-${i}`}>
                        <ArrowRightLeft className="w-3.5 h-3.5 mr-1" />Reemplazar
                      </Button>
                      <Button size="sm" variant="ghost" className="text-[#7a2514]"
                        onClick={() => eliminarLinea(sol, i, li)} data-testid={`eliminar-linea-${sol.numero}-${i}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      {reemplazo && (
        <ReemplazarProductoDialog
          sol={reemplazo.sol}
          linea={reemplazo.li}
          idx={reemplazo.idx}
          onClose={() => setReemplazo(null)}
          onSaved={() => { setReemplazo(null); load(); }}
        />
      )}
    </div>
  );
}

/** ITER221: reemplaza el producto pendiente por otro con stock (puede ser de otro laboratorio). */
function ReemplazarProductoDialog({ sol, linea, idx, onClose, onSaved }) {
  const [q, setQ] = useState("");
  const [opciones, setOpciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const buscar = async (term) => {
    setLoading(true);
    try {
      const qs = term && term.trim() ? `?q=${encodeURIComponent(term.trim())}` : "";
      const { data } = await api.get(`/sales/alternativas-producto/${linea.producto_comercial_id}${qs}`);
      setOpciones(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { buscar(""); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [linea.producto_comercial_id]);

  const vacio = useMemo(() => !loading && opciones.length === 0, [loading, opciones]);

  const aplicar = async (op) => {
    setSaving(true);
    try {
      await api.patch(`/sales/backorder/${sol.id}/reemplazar-producto`, {
        linea_idx: idx, nuevo_producto_comercial_id: op.producto_comercial_id,
      });
      toast.success(`Producto reemplazado por ${op.nombre_comercial}. Revise las cantidades del pendiente.`, { duration: 8000 });
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="reemplazar-producto-dialog">
        <DialogHeader>
          <DialogTitle>Reemplazar producto pendiente</DialogTitle>
        </DialogHeader>
        <div className="text-xs bg-[#f7f9f8] border border-[#d1dbd6] rounded p-2">
          Pendiente <strong>{sol.numero}</strong> · {linea.producto_nombre}
          {linea.laboratorio_nombre ? ` (${linea.laboratorio_nombre})` : ""} · {linea.cantidad_pendiente} {linea.unidad_venta}
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#8ba397]" />
          <Input value={q} className="pl-8"
            onChange={(e) => { setQ(e.target.value); buscar(e.target.value); }}
            placeholder="Buscar cualquier producto con stock (nombre, código, laboratorio)…"
            data-testid="reemplazar-buscar" />
          <div className="text-[11px] text-[#5c7368] mt-1">
            Sin texto se muestran los equivalentes por principio activo con stock; escriba para buscar cualquier producto.
          </div>
        </div>
        <div className="space-y-2">
          {loading && <div className="py-6 text-center text-sm text-[#8ba397]">Buscando…</div>}
          {vacio && (
            <div className="py-6 text-center text-sm text-[#8ba397]" data-testid="reemplazar-vacio">
              No hay productos con stock para reemplazar. Busque por nombre para ver otras opciones.
            </div>
          )}
          {opciones.map((op) => (
            <div key={op.producto_comercial_id}
              className="flex flex-wrap items-center justify-between gap-2 rounded border border-[#d1dbd6] bg-white p-2">
              <div className="min-w-0 text-xs">
                <div className="font-semibold text-[#15241d]">{op.nombre_comercial}</div>
                <div className="text-[11px] text-[#5c7368]">
                  {op.laboratorio || "—"}{op.presentacion ? ` · ${op.presentacion}` : ""}
                  {op.numero_lote ? ` · Lote ${op.numero_lote}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-right text-xs">
                  <div className="text-[#205c45] font-semibold">{op.stock_total} u.</div>
                  <div className="text-[11px] text-[#5c7368]">${(op.precio_unidad || 0).toFixed(2)} u.</div>
                </div>
                <Button size="sm" disabled={saving} className="bg-[#205c45] hover:bg-[#1a4a37]"
                  onClick={() => aplicar(op)} data-testid={`usar-reemplazo-${op.producto_comercial_id}`}>
                  <ArrowRightLeft size={13} className="mr-1" />Usar
                </Button>
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
